import { Outlet, useNavigate, useParams } from "@tanstack/react-router";
import { PrinterIcon } from "lucide-react";
import { type Key, useEffect, useState } from "react";

import { trpc } from "@blis/trpc/react";

import { CommandMenu, CommandMenuProvider } from "@/components/CommandMenu";
import { LoadingPage } from "@/components/LoadingPage";
import { PageTitle } from "@/components/PageTitle";
import { SubHeader } from "@/components/SubHeader";
import { Breadcrumb, Breadcrumbs } from "@/components/ui/breadcrumbs";
import { Centered } from "@/components/ui/container";
import { NavList, NavListItem } from "@/components/ui/nav-list";
import { useInitializeNavStack } from "@/utils/useNavStack";
import { PrintWorklistsButton } from "../components/PrintWorklistsButton";
import { WorklistAccessionItem } from "../components/WorklistAccessionItem";
import { useWorklist, WorklistProvider } from "../components/WorklistProvider";
import type { TestRunDetails, WorklistNew } from "../types";
import { worklistNameWithCodes } from "../utils";

export function WorklistPage() {
  useInitializeNavStack();
  const params = useParams({
    from: "/_auth/_app/runs/$id/worklist/$testId",
  });
  const id = Number(params.id);

  const run = trpc.runs.get.useQuery({ id });
  const worklists = trpc.runs.worklists.useQuery({ id });

  if (run.isPending || worklists.isPending) {
    return <LoadingPage />;
  }

  if (!run.data) {
    return <Centered>Test run not found</Centered>;
  }

  const worklist = (worklists.data || []).find(
    (w) =>
      w.id === params.testId ||
      w.originalTestSpecs.some((t) => t.id === params.testId),
  );

  if (!worklist) {
    return <Centered>Worklist not found</Centered>;
  }

  return (
    <>
      <PageTitle>{`Worklist - ${worklistNameWithCodes(worklist)}`}</PageTitle>
      <SubHeader>
        <SubHeader.Left>
          <Breadcrumbs>
            <Breadcrumb href={{ to: "/runs" }}>Test runs</Breadcrumb>
            <Breadcrumb href={{ to: "/runs/$id", params: { id } }}>
              {params.id}
            </Breadcrumb>
            <Breadcrumb>{worklistNameWithCodes(worklist)}</Breadcrumb>
          </Breadcrumbs>
        </SubHeader.Left>
        <SubHeader.Right>
          <PrintWorklistsButton
            run={run.data}
            worklists={[worklist]}
            variant="outline"
            size="sm"
          >
            <PrinterIcon />
            Print
          </PrintWorklistsButton>
        </SubHeader.Right>
      </SubHeader>
      <WorklistProvider
        run={run.data}
        worklist={worklist}
        orders={worklist.testOrders}
      >
        <AccessionSync orders={worklist.testOrders} />
        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1 flex flex-col overflow-y-auto border-r">
            <AccessionList worklist={worklist} run={run.data} />
          </div>
          <Outlet />
        </div>
      </WorklistProvider>
      <CommandMenuProvider>
        <CommandMenu />
      </CommandMenuProvider>
    </>
  );
}

function AccessionList({
  worklist,
  run,
}: {
  worklist: WorklistNew;
  run: TestRunDetails;
}) {
  const navigate = useNavigate();
  const params: { orderId?: number } = useParams({ strict: false });
  const [selected, setSelected] = useState<Key[]>([]);

  useEffect(() => {
    if (params.orderId) {
      setSelected([params.orderId]);
    } else {
      setSelected([]);
    }
  }, [params.orderId]);

  return (
    <NavList
      items={worklist.testOrders}
      aria-label="Worklist items"
      autoFocus
      // hideCheckbox
      // bottomBorder
      disallowEmptySelection
      selectedKeys={selected}
      onSelectionChange={(keys) => {
        const key = Array.from(keys)[0];
        if (!key) {
          setSelected([]);
          return;
        }
        setSelected([key]);
        const replace = !!params.orderId;
        navigate({
          from: "/runs/$id/worklist/$testId",
          to: "/runs/$id/worklist/$testId/results/$orderId",
          params: { id: run.id, testId: worklist.id, orderId: Number(key) },
          replace,
          state: {
            href: {
              to: "/runs/$id/worklist/$testId",
              params: { id: run.id, testId: worklist.id },
            },
          },
        });
      }}
      selectionMode="single"
    >
      {(order) => (
        <NavListItem key={order.id} id={order.id} asChild>
          <WorklistAccessionItem order={order} />
        </NavListItem>
      )}
    </NavList>
  );
}

function AccessionSync({ orders }: { orders: WorklistNew["testOrders"] }) {
  const params: { orderId?: number } = useParams({ strict: false });
  const { setOrder } = useWorklist();

  useEffect(() => {
    if (params.orderId) {
      setOrder(
        orders.find((order) => order.id === Number(params.orderId)) || null,
      );
    }
  }, [params.orderId, orders, setOrder]);

  return null;
}
